import React, { useEffect, useState, useCallback } from 'react';
import { useSimulation } from '../../hooks/useSimulation';
import { useIsometricSimulation } from './IsometricOffice/useIsometricSimulation';
import { IsometricCanvas } from './IsometricOffice/IsometricCanvas';
import { CartoonCanvas } from './IsometricOffice/CartoonCanvas';
import { MorningCheckInModal } from './IsometricOffice/MorningCheckInModal';
import { EveningDebriefModal } from './IsometricOffice/EveningDebriefModal';
import { SimulationControls } from './SimulationControls';
import { EventTimeline } from './EventTimeline';
import { AlertCircle, Loader2, Monitor, Clock, Zap, Bell, AlertTriangle, CheckCircle } from 'lucide-react';
import { MorningApprovalData, EveningDebriefData } from '../../types/isometric.types';

interface SimulationViewProps {
  planId: string;
  mundusId?: string;
  mundusName?: string;
  autoStart?: boolean;
  useIsometric?: boolean;
  useCartoonStyle?: boolean;
}

export const SimulationView: React.FC<SimulationViewProps> = ({
  planId,
  mundusId,
  mundusName,
  autoStart = false,
  useIsometric = true,
  useCartoonStyle = true,
}) => {
  // Original simulation hook for event-driven updates
  const { state: legacyState, events, isLoading, error, actions: legacyActions } = useSimulation({
    planId,
    autoConnect: true,
  });

  // New isometric simulation hook
  const {
    state: isoState,
    currentCheckpoint,
    actions: isoActions
  } = useIsometricSimulation({
    planId,
    mundusId,
  });

  // Checkpoint modal state
  const [morningModalOpen, setMorningModalOpen] = useState(false);
  const [eveningModalOpen, setEveningModalOpen] = useState(false);
  const [morningData, setMorningData] = useState<MorningApprovalData | null>(null);
  const [eveningData, setEveningData] = useState<EveningDebriefData | null>(null);
  const [checkpointTimeRemaining, setCheckpointTimeRemaining] = useState(1800); // 30 min default

  // Monitor for checkpoint triggers from isometric state
  useEffect(() => {
    if (isoState.showMorningModal && !morningModalOpen) {
      // Fetch morning data and open modal
      setMorningData({
        todaysPlan: {
          id: 'plan-today',
          title: 'Daily Action Plan',
          description: 'AI-generated plan based on your goals and current progress',
          actions: [
            { id: '1', title: 'Review and respond to priority communications', priority: 'HIGH', estimatedDuration: 30 },
            { id: '2', title: 'Continue skill development activities', priority: 'MEDIUM', estimatedDuration: 60 },
            { id: '3', title: 'Network outreach - connect with 2 new contacts', priority: 'MEDIUM', estimatedDuration: 45 },
            { id: '4', title: 'Research target opportunities', priority: 'HIGH', estimatedDuration: 45 },
          ],
        },
        yesterdaySummary: 'Completed 3 of 4 planned actions. Network growth on track.',
        carryoverTasks: ['Follow up with contact from previous outreach'],
        suggestedPriorities: [
          'Focus on high-priority networking today to maintain momentum',
          'Consider scheduling a skill assessment review',
        ],
      });
      setCheckpointTimeRemaining(1800);
      setMorningModalOpen(true);
    }

    if (isoState.showEveningModal && !eveningModalOpen) {
      // Fetch evening data and open modal
      setEveningData({
        accomplishments: [
          { title: 'Completed morning communications review', status: 'COMPLETED' },
          { title: 'Skill development session - 45 min', status: 'COMPLETED' },
          { title: 'Network outreach - 2 connections made', status: 'COMPLETED' },
          { title: 'Opportunity research', status: 'PARTIAL' },
        ],
        metricsChanges: [
          { name: 'Pipeline Score', previousValue: 72, currentValue: 78, change: 6 },
          { name: 'Skills Gap', previousValue: 38, currentValue: 35, change: -3 },
          { name: 'Network Growth', previousValue: 10, currentValue: 12, change: 2 },
        ],
        challenges: ['Limited time for deep research due to unexpected meetings'],
        tomorrowPreview: 'Focus on completing opportunity research and continuing skill development.',
      });
      setCheckpointTimeRemaining(3600);
      setEveningModalOpen(true);
    }
  }, [isoState.showMorningModal, isoState.showEveningModal, morningModalOpen, eveningModalOpen]);

  // Countdown timer for checkpoint modals
  useEffect(() => {
    if (morningModalOpen || eveningModalOpen) {
      const interval = setInterval(() => {
        setCheckpointTimeRemaining((prev) => {
          if (prev <= 1) {
            // Auto-proceed
            if (morningModalOpen) {
              handleMorningSkip();
            } else {
              handleEveningSkip();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [morningModalOpen, eveningModalOpen]);

  // Morning checkpoint handlers
  const handleMorningApprove = useCallback((comments?: string) => {
    isoActions.respondToCheckpoint({ approved: true, comments });
    setMorningModalOpen(false);
  }, [isoActions]);

  const handleMorningAdjust = useCallback((adjustments: string[]) => {
    isoActions.respondToCheckpoint({ approved: true, adjustments });
    setMorningModalOpen(false);
  }, [isoActions]);

  const handleMorningSkip = useCallback(() => {
    isoActions.respondToCheckpoint({ approved: true, comments: 'Auto-approved' });
    setMorningModalOpen(false);
  }, [isoActions]);

  // Evening checkpoint handlers
  const handleEveningFeedback = useCallback((feedback: { rating: 1 | 2 | 3 | 4 | 5; feedback: string }) => {
    isoActions.respondToCheckpoint({ rating: feedback.rating, feedback: feedback.feedback });
    setEveningModalOpen(false);
  }, [isoActions]);

  const handleEveningSkip = useCallback(() => {
    isoActions.respondToCheckpoint({ rating: 3, feedback: 'Skipped feedback' });
    setEveningModalOpen(false);
  }, [isoActions]);

  // Unified actions that work with both legacy and isometric
  const handleStart = useCallback(() => {
    if (useIsometric) {
      isoActions.start();
    }
    legacyActions.start();
  }, [useIsometric, isoActions, legacyActions]);

  const handleStop = useCallback(() => {
    if (useIsometric) {
      isoActions.stop();
    }
    legacyActions.stop();
  }, [useIsometric, isoActions, legacyActions]);

  const handlePause = useCallback(() => {
    if (useIsometric) {
      isoActions.pause();
    }
    legacyActions.pause();
  }, [useIsometric, isoActions, legacyActions]);

  const handleResume = useCallback(() => {
    if (useIsometric) {
      isoActions.resume();
    }
    legacyActions.resume();
  }, [useIsometric, isoActions, legacyActions]);

  const handleSpeedChange = useCallback((speed: number) => {
    // Validate and cast speed value to proper type
    const validSpeeds = [1, 2, 4, 8] as const;
    const validSpeed = validSpeeds.includes(speed as 1 | 2 | 4 | 8)
      ? (speed as 1 | 2 | 4 | 8)
      : 1;

    if (useIsometric) {
      isoActions.setSpeed(validSpeed);
    }
    legacyActions.setSpeed(validSpeed);
  }, [useIsometric, isoActions, legacyActions]);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart && !legacyState && !isLoading) {
      handleStart();
    }
  }, [autoStart, legacyState, isLoading, handleStart]);

  return (
    <div className="space-y-6">
      {/* Checkpoint Modals */}
      <MorningCheckInModal
        isOpen={morningModalOpen}
        checkpoint={currentCheckpoint}
        data={morningData}
        timeRemaining={checkpointTimeRemaining}
        onApprove={handleMorningApprove}
        onAdjust={handleMorningAdjust}
        onSkip={handleMorningSkip}
        onClose={() => setMorningModalOpen(false)}
      />

      <EveningDebriefModal
        isOpen={eveningModalOpen}
        checkpoint={currentCheckpoint}
        data={eveningData}
        timeRemaining={checkpointTimeRemaining}
        onSubmitFeedback={handleEveningFeedback}
        onSkip={handleEveningSkip}
        onClose={() => setEveningModalOpen(false)}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-red-900 mb-1">Unable to Start Simulation</h4>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => window.history.back()}
              className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium underline"
            >
              Go back to plan
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && !legacyState && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
          <p className="text-sm text-blue-900">Loading simulation...</p>
        </div>
      )}

      {/* Isometric Status Bar */}
      {useIsometric && (
        <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="font-mono text-lg">
                {isoState.currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
              <span className="text-slate-400 text-sm capitalize">
                ({isoState.timeOfDay.toLowerCase()})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-sm">{isoState.speed}x speed</span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4 text-green-400" />
              <span className="text-sm">{isoState.agents.length} agents active</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isoState.activeAlarms.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 rounded-full text-red-400 text-sm animate-pulse">
                <span className="w-2 h-2 bg-red-500 rounded-full" />
                {isoState.activeAlarms.length} Active Alarm(s)
              </div>
            )}
            <div className={`px-3 py-1 rounded-full text-sm ${
              isoState.isRunning
                ? isoState.isPaused
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-green-500/20 text-green-400'
                : 'bg-slate-500/20 text-slate-400'
            }`}>
              {isoState.isRunning ? (isoState.isPaused ? 'Paused' : 'Running') : 'Stopped'}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <SimulationControls
        state={legacyState}
        isLoading={isLoading}
        onStart={handleStart}
        onStop={handleStop}
        onPause={handlePause}
        onResume={handleResume}
        onSpeedChange={handleSpeedChange}
      />

      {/* Simulation Canvas - Full Width */}
      <div className="w-full">
        {useIsometric ? (
          useCartoonStyle ? (
            <CartoonCanvas
              state={isoState}
              mundusName={mundusName}
              className="w-full"
              onAgentClick={(agent) => console.log('Agent clicked:', agent)}
              onKPIClick={(kpi) => console.log('KPI clicked:', kpi)}
            />
          ) : (
            <IsometricCanvas
              state={isoState}
              className="w-full"
              onAgentClick={(agent) => console.log('Agent clicked:', agent)}
              onKPIClick={(kpi) => console.log('KPI clicked:', kpi)}
            />
          )
        ) : (
          // Fallback to legacy canvas - commented out for now
          <div className="bg-slate-800 rounded-lg p-8 text-center text-slate-400">
            Legacy simulation canvas (Pokemon style) is disabled.
            Enable isometric mode for the new modern office visualization.
          </div>
        )}
      </div>

      {/* KPI and Alarms Panels - Side by Side */}
      {useIsometric && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Real-Time KPIs Panel */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Monitor className="w-4 h-4 text-blue-600" />
              Real-Time KPIs
            </h4>
            {isoState.kpis.length > 0 ? (
              <div className="space-y-4">
                {isoState.kpis.map((kpi) => (
                  <div
                    key={kpi.id}
                    className={`p-4 rounded-lg border ${
                      kpi.alarmActive
                        ? 'border-red-300 bg-red-50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{kpi.name}</span>
                      <span className={`text-lg font-bold ${
                        kpi.status === 'EXCELLENT' ? 'text-green-600' :
                        kpi.status === 'GOOD' ? 'text-blue-600' :
                        kpi.status === 'WARNING' ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {kpi.value}/{kpi.target} {kpi.unit}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          kpi.status === 'EXCELLENT' ? 'bg-green-500' :
                          kpi.status === 'GOOD' ? 'bg-blue-500' :
                          kpi.status === 'WARNING' ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, (kpi.value / kpi.target) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className={`${
                        kpi.trend === 'UP' ? 'text-green-600' :
                        kpi.trend === 'DOWN' ? 'text-red-600' :
                        'text-slate-500'
                      }`}>
                        {kpi.trend === 'UP' ? '↑ Trending up' :
                         kpi.trend === 'DOWN' ? '↓ Trending down' :
                         '→ Stable'}
                      </span>
                      {kpi.alarmActive && (
                        <span className="text-red-600 font-medium animate-pulse">
                          ⚠ Alarm Active
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No KPIs configured</p>
              </div>
            )}
          </div>

          {/* Alarms Panel */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4 text-red-600" />
              Alarms
              {isoState.activeAlarms.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full animate-pulse">
                  {isoState.activeAlarms.length} Active
                </span>
              )}
            </h4>

            {/* Active Alarms */}
            {isoState.activeAlarms.length > 0 ? (
              <div className="space-y-3">
                {isoState.kpis
                  .filter(kpi => kpi.alarmActive)
                  .map((kpi) => (
                    <div
                      key={`alarm-${kpi.id}`}
                      className="p-4 rounded-lg border-2 border-red-300 bg-red-50 animate-pulse"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-red-900">{kpi.name} Alert</h5>
                          <p className="text-sm text-red-700 mt-1">
                            Current value ({kpi.value} {kpi.unit}) is below target ({kpi.target} {kpi.unit})
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-1 bg-red-200 text-red-800 rounded">
                              {kpi.status}
                            </span>
                            <span className="text-xs text-red-600">
                              {kpi.trend === 'DOWN' ? '↓ Declining' : kpi.trend === 'UP' ? '↑ Improving' : '→ Stable'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => isoActions.clearAlarm(kpi.id)}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        >
                          Acknowledge
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p className="font-medium text-green-600">All Systems Normal</p>
                <p className="text-sm text-slate-500 mt-1">No active alarms</p>
              </div>
            )}

            {/* Alarm History / Recent */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Alarm Thresholds
              </h5>
              <div className="space-y-2">
                {isoState.kpis.map((kpi) => (
                  <div
                    key={`threshold-${kpi.id}`}
                    className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-sm"
                  >
                    <span className="text-slate-700">{kpi.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">
                        Trigger: &lt; {Math.round(kpi.target * 0.5)} {kpi.unit}
                      </span>
                      <span className={`w-2 h-2 rounded-full ${
                        kpi.alarmActive ? 'bg-red-500 animate-pulse' : 'bg-green-500'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Timeline - Collapsible Section Below */}
      <details className="bg-white rounded-lg shadow-sm" open>
        <summary className="cursor-pointer p-4 font-semibold text-slate-900 hover:bg-slate-50">
          Event Timeline ({events.length} events)
        </summary>
        <div className="p-4 pt-0">
          <EventTimeline events={events} className="max-h-60 overflow-y-auto" />
        </div>
      </details>

      {/* Agent Legend - Enhanced for Isometric */}
      {useIsometric && isoState.agents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Active Agents</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {isoState.agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md"
                  style={{ backgroundColor: agent.style.primaryColor }}
                >
                  {agent.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{agent.name}</p>
                  <p className="text-xs text-slate-500 capitalize">
                    {agent.role.toLowerCase()} • {agent.activity.toLowerCase().replace('_', ' ')}
                  </p>
                  {agent.currentTask && (
                    <p className="text-xs text-blue-600 truncate mt-1">
                      {agent.currentTask}
                    </p>
                  )}
                </div>
                {agent.activityProgress !== undefined && agent.activityProgress > 0 && (
                  <div className="w-12 h-12 relative">
                    <svg className="transform -rotate-90 w-12 h-12">
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke="#E2E8F0"
                        strokeWidth="4"
                      />
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke={agent.style.primaryColor}
                        strokeWidth="4"
                        strokeDasharray={`${(agent.activityProgress / 100) * 126} 126`}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                      {Math.round(agent.activityProgress)}%
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy Agent Legend */}
      {!useIsometric && legacyState && legacyState.agentPositions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Agents</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {legacyState.agentPositions.map((pos, index) => (
              <div
                key={pos.agentId}
                className="flex items-center gap-2 p-2 rounded-md bg-slate-50"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{
                    backgroundColor: [
                      '#3B82F6',
                      '#10B981',
                      '#F59E0B',
                      '#EF4444',
                      '#8B5CF6',
                      '#EC4899',
                    ][index % 6],
                  }}
                >
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">
                    Agent {index + 1}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {pos.location.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
